import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Tooltip from './Tooltip';
import { subscriptionApi } from '../utils/apiUtils';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Pricing = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = user !== null;

  useEffect(() => {
    const fetchProducts = async () => {
      const response = await subscriptionApi.getProducts();
      setProducts(response.products);
    };
    fetchProducts();
  }, []);

  const handleProductSelect = (product) => setSelectedProduct(product);

  const handleCheckout = async (product) => {
    if (!product) return;
    try {
      setLoading(true);
      const response = await subscriptionApi.createCheckoutSession(product);
      if (response.redirect) {
        navigate(response.redirect);
      } else if (response.sessionId) {
        const stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: response.sessionId });
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setLoading(false);
    }
  };

  // Free plan is the floor — a user whose Stripe sync is still in-flight has
  // no tier yet, but they're implicitly on free_plan. Render the button that
  // way so new users immediately see "Current Plan" on Free.
  const effectiveTier = isLoggedIn ? (user.stripeSubscriptionTier || 'free_plan') : null;

  const renderButton = (product) => {
    let buttonText = 'Select Plan';
    let onClick = () => handleProductSelect(product);
    let tooltipContent = '';
    let isDisabled = false;

    if (loading) { buttonText = 'Loading...'; isDisabled = true; }
    else if (isLoggedIn && effectiveTier === product.lookup_key) { buttonText = 'Current Plan'; isDisabled = true; }
    else if (!isLoggedIn) { onClick = () => navigate('/sign-in'); tooltipContent = 'Sign in to subscribe'; }
    else if (selectedProduct?.id === product.id) { buttonText = 'Proceed to Checkout'; onClick = () => handleCheckout(product); }

    return (
      <Tooltip content={tooltipContent}>
        <button
          className={`w-full py-3 px-4 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
            isDisabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : selectedProduct?.id === product.id
                ? 'bg-yellow-300 text-gray-900 hover:bg-yellow-200 shadow-glow-yellow'
                : 'bg-purple-600 text-white hover:bg-purple-700 shadow-glow-purple'
          }`}
          onClick={onClick}
          disabled={isDisabled}
        >
          {buttonText}
        </button>
      </Tooltip>
    );
  };

  return (
    <div className="max-w-5xl mx-auto py-8 sm:py-16">
      <motion.div
        className="text-center mb-14"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <span className="inline-block text-[10px] text-yellow-300/70 uppercase tracking-[0.25em] font-semibold mb-3">
          // pricing
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-3">Choose Your Plan</h1>
        <p className="text-white/40 text-sm max-w-md mx-auto">Unlock the full potential of your research workflow</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((product, index) => {
          const isCurrent = isLoggedIn && effectiveTier === product.lookup_key;
          const isSelected = selectedProduct?.id === product.id;
          const isMiddle = index === 1;

          return (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative p-6 sm:p-8 rounded-2xl transition-all duration-300 flex flex-col ${
                isCurrent
                  ? 'bg-white shadow-card-hover ring-2 ring-yellow-300'
                  : isSelected
                    ? 'bg-white shadow-card-hover ring-2 ring-purple-400'
                  : isMiddle
                    ? 'bg-white shadow-glass-lg scale-[1.02]'
                    : 'bg-white/95 hover:shadow-glass hover:scale-[1.01]'
              }`}
            >
              {isMiddle && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-yellow-300 text-gray-900 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                    Popular
                  </span>
                </div>
              )}
              <div className="flex-grow">
                <h2 className="text-base font-bold mb-1 text-gray-900">{product.name}</h2>
                <p className="text-3xl font-extrabold mb-6 text-purple-600 tracking-tight">
                  {product.price === 'Custom'
                    ? 'Custom'
                    : new Intl.NumberFormat('en', {
                        style: 'currency',
                        currency: (product.currency || 'eur').toUpperCase(),
                        minimumFractionDigits: 0
                      }).format(product.price / 100)}
                  {product.price !== 'Custom' && <span className="text-sm font-normal text-gray-400 ml-0.5">/mo</span>}
                </p>
                <ul className="text-xs text-gray-600 mb-8 space-y-2.5">
                  {product.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-emerald-500 mr-2 mt-0.5 text-[10px] font-bold">&#10003;</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto">
                {renderButton(product)}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default Pricing;
